import React, { PropsWithChildren } from 'react';
import Bugsnag from '@bugsnag/js';
import BugsnagPluginReact from '@bugsnag/plugin-react';
import ErrorPage from 'next/error';
import { NextPageContext } from 'next';

const bugsnagApiKey = process.env.NEXT_PUBLIC_BUGSNAG_API_KEY;
const isValidBugsnagApiKey =
  typeof bugsnagApiKey === 'string' && /^[0-9a-f]{32}$/i.test(bugsnagApiKey);
export const bugsnagEnabled = process.env.NODE_ENV !== 'development' && isValidBugsnagApiKey;

const releaseStage = process.env.NEXT_PUBLIC_ENV || process.env.NODE_ENV;

const getErrorMessages = (event: any) =>
  (event.errors || [])
    .map((error: any) => `${error.errorClass || ''} ${error.errorMessage || ''}`.toLowerCase())
    .join(' ');

const getStackTraceSource = (event: any) =>
  (event.errors || [])
    .flatMap((error: any) => error.stacktrace || [])
    .map((frame: any) => `${frame.file || ''} ${frame.method || ''}`.toLowerCase())
    .join(' ');

const getRequestUrl = (event: any) => `${event.request?.url || ''}`.toLowerCase();

const hasAny = (value: string, needles: string[]) =>
  needles.some((needle) => value.includes(needle));

const shouldIgnoreXhrBlobResponseTextError = (event: any) => {
  return event.errors?.some(
    (error: any) =>
      error.errorClass === 'InvalidStateError' &&
      error.errorMessage?.includes('responseText') &&
      error.errorMessage?.includes('blob'),
  );
};

const shouldIgnoreBazaarvoiceAnalyticsTimeout = (event: any) => {
  const message = getErrorMessages(event);
  const stackTraceSource = getStackTraceSource(event);
  const requestUrl = getRequestUrl(event);
  const hasBazaarvoiceSource =
    hasAny(message, ['bv-analytics.js', 'apps.bazaarvoice.com']) ||
    hasAny(stackTraceSource, ['bv-analytics.js', 'apps.bazaarvoice.com']) ||
    hasAny(requestUrl, ['bv-analytics.js', 'apps.bazaarvoice.com']);

  return hasBazaarvoiceSource && hasAny(message, ['script timeout', 'could not load']);
};

const shouldIgnoreBrowserExtensionNoise = (event: any) => {
  const message = getErrorMessages(event);
  const stackTraceSource = getStackTraceSource(event);

  return (
    hasAny(stackTraceSource, ['chrome-extension://', 'moz-extension://', 'safari-extension://']) ||
    hasAny(message, ['resizeobserver loop limit exceeded'])
  );
};

const shouldIgnoreSameUrlNavigationError = (event: any) => {
  const message = getErrorMessages(event);

  return message.includes('attempted to navigate to the same url');
};

const shouldIgnoreSwiperNoise = (event: any) => {
  const message = getErrorMessages(event);
  const stackTraceSource = getStackTraceSource(event);
  const hasSwiperSource = hasAny(message, ['swiper']) || hasAny(stackTraceSource, ['swiper']);

  return (
    hasSwiperSource &&
    hasAny(message, [
      'addcallback',
      'handleevent',
      'is undefined',
      'undefined is not an object',
      "evaluating 'r.geometry'",
    ])
  );
};

const shouldIgnoreEvent = (event: any) => {
  return (
    event.errors.some(
      (error: any) =>
        error.errorMessage.startsWith('Minified React error #418') ||
        error.errorMessage.startsWith('Minified React error #423'),
    ) ||
    shouldIgnoreXhrBlobResponseTextError(event) ||
    shouldIgnoreBazaarvoiceAnalyticsTimeout(event) ||
    shouldIgnoreBrowserExtensionNoise(event) ||
    shouldIgnoreSameUrlNavigationError(event) ||
    shouldIgnoreSwiperNoise(event)
  );
};

let started = false;
if (!started && bugsnagEnabled) {
  started = true;
  const apiKey = bugsnagApiKey as string;
  Bugsnag.start({
    onError: (event) =>
      new Promise((resolve) => {
        if (shouldIgnoreEvent(event)) {
          resolve(false);
        } else {
          resolve(true);
        }
      }),
    enabledReleaseStages: ['test', 'qa', 'preprod', 'production'],
    enabledBreadcrumbTypes: ['navigation', 'user', 'state', 'log', 'error', 'process'],
    apiKey,
    plugins: [new BugsnagPluginReact(React)],
    releaseStage,
  });
}

const Error = class Page extends React.Component<{
  error: Error;
  info: React.ErrorInfo;
  clearError: () => void;
}> {
  static async getInitialProps(ctx: NextPageContext) {
    if (ctx.err && bugsnagEnabled) Bugsnag.notify(ctx.err);
    return ErrorPage.getInitialProps(ctx);
  }
  render() {
    return <ErrorPage statusCode={500} />;
  }
};

const ErrorBoundary = ({ children }: PropsWithChildren) => {
  if (!bugsnagEnabled) {
    return <>{children}</>;
  }

  const BugsnagBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary();
  return <BugsnagBoundary FallbackComponent={Error}>{children}</BugsnagBoundary>;
};

export default ErrorBoundary;
